import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { PreviewTable } from '../../../../components/PreviewTable';
import { DateField } from '../../../../components/inputs/DateField';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { ApiError } from '../../../../lib/apiError';
import { focusValidationError } from '../../../../lib/validationFocus';
import { inventoryItemsApi, storageFacilitiesApi } from '../inventoryApi';
import type { InventoryItemOption, StorageFacilityOption } from '../inventoryTypes';
import { stockBatchesApi } from '../stockApi';
import type { StockBatchDetail, StockBatchStatus } from '../stockTypes';
import { BATCH_STATUS, BATCH_STATUS_LABEL } from '../stockTypes';
import { sampleStockBatch } from '../utils/sampleStock';

function toNumberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  return trimmed === '' ? null : Number(trimmed);
}

/**
 * The states a reader may put a lot in.
 *
 * <b>Expired and Depleted are absent, and both for the same reason read two ways.</b> Expired
 * is derived from the date on every read, so storing it would let a row claim a state that
 * clears itself the moment somebody corrects the date; Depleted follows from the quantity
 * reaching zero, so setting it by hand would let a lot holding forty report as empty. The
 * server refuses both — this is the form not offering what it knows would be refused.
 */
const SETTABLE_STATUSES: StockBatchStatus[] = [
  BATCH_STATUS.active,
  BATCH_STATUS.quarantined,
  BATCH_STATUS.damaged,
];

/**
 * Receive a lot, or correct one.
 *
 * **Create and edit are different forms here, not one shape with fields disabled.** Receiving
 * asks which item and which facility and how much arrived; correcting asks none of those — the
 * item says what the stock IS and moving it between facilities is a transfer, which this slice
 * does not build. A shared shape would make all three editable, and a request that CAN express
 * a thing is one that will eventually perform it by accident.
 */
export function StockBatchModal({
  open,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** Null receives a new lot. */
  editing: StockBatchDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = editing !== null;

  const [itemId, setItemId] = useState(editing?.inventoryItemId ?? '');
  const [facilityId, setFacilityId] = useState(editing?.storageFacilityId ?? '');
  const [batchNumber, setBatchNumber] = useState(editing?.batchNumber ?? '');
  const [quantity, setQuantity] = useState(String(editing?.quantityOnHand ?? ''));
  const [expiryDate, setExpiryDate] = useState(editing?.expiryDate ?? '');
  const [unitCost, setUnitCost] = useState(
    editing?.unitCost === null || editing?.unitCost === undefined ? '' : String(editing.unitCost),
  );
  const [status, setStatus] = useState(String(editing?.status ?? BATCH_STATUS.active));
  const [quarantineReason, setQuarantineReason] = useState(editing?.quarantineReason ?? '');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [notes, setNotes] = useState(editing?.notes ?? '');

  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [facilities, setFacilities] = useState<StorageFacilityOption[]>([]);
  // TRUE on the first render: the fetch starts in an effect, which runs AFTER paint.
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || isEdit) return;

    const controller = new AbortController();
    setLoadingCatalogs(true);

    Promise.all([
      inventoryItemsApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([loadedItems, loadedFacilities]) => {
        setItems(loadedItems);
        setFacilities(loadedFacilities);
      })
      // A failed picker load names no field, so it must not go through the save path's error
      // state — the fields render empty rather than the form reporting a rejection.
      .catch(() => undefined)
      .finally(() => setLoadingCatalogs(false));

    return () => controller.abort();
  }, [open, isEdit]);

  /*
    A SERIALIZED item is recorded one row per thing, so a lot of one would be a quantity nothing
    can reconcile against the units beside it. Offering only what can be chosen is the picker
    rule: a control that lists values the server will refuse is a control that produces
    rejections.
  */
  const offeredItems = items.filter((i) => !i.tracksSerial);

  const chosenItem = items.find((i) => i.id === itemId) ?? null;
  const quantityNumber = Number(quantity);
  const quantityMoved = isEdit && quantityNumber !== editing.quantityOnHand;
  const quarantining = Number(status) === BATCH_STATUS.quarantined;

  const valid =
    batchNumber.trim().length > 0 &&
    quantity.trim() !== '' &&
    Number.isFinite(quantityNumber) &&
    (isEdit ? quantityNumber >= 0 : quantityNumber > 0) &&
    (isEdit || (itemId !== '' && facilityId !== '')) &&
    (!quantityMoved || adjustmentReason.trim().length > 0) &&
    (!quarantining || quarantineReason.trim().length > 0);

  const canSave = valid && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      if (editing) {
        await stockBatchesApi.update(editing.id, {
          batchNumber: batchNumber.trim(),
          quantityOnHand: quantityNumber,
          expiryDate: expiryDate || null,
          unitCost: toNumberOrNull(unitCost),
          status: Number(status) as StockBatchStatus,
          quarantineReason: quarantining ? quarantineReason.trim() || null : null,
          adjustmentReason: quantityMoved ? adjustmentReason.trim() : null,
          notes: notes.trim() || null,
        });
      } else {
        await stockBatchesApi.create({
          inventoryItemId: itemId,
          storageFacilityId: facilityId,
          batchNumber: batchNumber.trim(),
          quantity: quantityNumber,
          expiryDate: expiryDate || null,
          unitCost: toNumberOrNull(unitCost),
          notes: notes.trim() || null,
        });
      }

      onSaved();
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save the batch.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: 'stock-batch' });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit batch' : 'Receive stock'}
      size="lg"
      id="stock-batch-modal"
      loading={loadingCatalogs}
      autogenerate={
        isEdit
          ? undefined
          : {
              onGenerate: () => {
                const sample = sampleStockBatch({ items, facilities });

                setItemId(sample.inventoryItemId);
                setFacilityId(sample.storageFacilityId);
                setBatchNumber(sample.batchNumber);
                setQuantity(sample.quantity);
                setExpiryDate(sample.expiryDate);
                setUnitCost(sample.unitCost);
                setNotes(sample.notes);
              },
              disabled: saving || loadingCatalogs,
            }
      }
      confirm={{
        id: 'stock-batch-confirm',
        checked: confirmed,
        onChange: setConfirmed,
        disabled: saving,
      }}
      footer={
        <>
          <Button id="stock-batch-cancel" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            id="stock-batch-submit"
            variant="primary"
            onClick={() => void save()}
            disabled={!canSave}
            loading={saving}
            loadingLabel="Submitting"
          >
            Submit
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {isEdit ? (
          /*
            What the lot IS, stated rather than asked. The reader cannot re-point a batch at
            another item or move it to another facility from here — that is a transfer — so
            these are read-only FACTS and take the Table Display View, not disabled pickers.
          */
          <PreviewTable
            variant="table-display"
            rows={[
              { label: 'Item', value: `${editing.sku} — ${editing.itemName}` },
              { label: 'Facility', value: editing.storageFacilityName },
              { label: 'Received', value: `${editing.quantityReceived} ${editing.unitCode}`, mono: true },
            ]}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            <Select
              id="stock-batch-inventory-item-id"
              label="Item"
              required
              description="What this lot is of. Only items counted as a quantity are offered — a serialized item is recorded as units instead."
              placeholder="Choose an item"
              value={itemId}
              onChange={setItemId}
              options={offeredItems.map((i) => ({
                value: i.id,
                label: i.name,
                sublabel: i.sku,
                right: i.unitCode,
                keywords: i.sku,
              }))}
              remote
              loading={loadingCatalogs}
              disabled={saving}
              error={error?.fieldError('inventoryItemId')}
            />

            <Select
              id="stock-batch-storage-facility-id"
              label="Facility"
              required
              description="Where this lot physically is. Moving it later is a transfer, which this module does not yet record."
              placeholder="Choose a facility"
              value={facilityId}
              onChange={setFacilityId}
              options={facilities.map((f) => ({ value: f.id, label: f.name, sublabel: f.code }))}
              remote
              loading={loadingCatalogs}
              disabled={saving}
              error={error?.fieldError('storageFacilityId')}
            />
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            id="stock-batch-batch-number"
            label="Batch number"
            required
            description="The lot number as the supplier wrote it. It is what a recall traceback quotes, so it is worth keying exactly."
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
            maxLength={80}
            disabled={saving}
            error={error?.fieldError('batchNumber')}
          />

          <TextField
            id={isEdit ? 'stock-batch-quantity-on-hand' : 'stock-batch-quantity'}
            label={isEdit ? 'Quantity on hand' : 'Quantity received'}
            required
            type="number"
            min={isEdit ? 0 : 1}
            description={
              isEdit
                ? 'The corrected figure, not a change. The difference is written to the ledger as an adjustment.'
                : 'How much arrived. It opens the ledger for this lot as a receipt, and sets both quantity columns.'
            }
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            disabled={saving}
            error={error?.fieldError(isEdit ? 'quantityOnHand' : 'quantity')}
          />
        </div>

        {/*
          Drawn only when the quantity actually moved, and required then. A correction with no
          stated cause is a ledger line nobody can interpret — and unlike most conditional
          fields this one appears in response to something the reader just typed, so it is not
          a field arriving out of nowhere.
        */}
        {quantityMoved ? (
          <TextField
            id="stock-batch-adjustment-reason"
            label="Why did the quantity change?"
            required
            description="Recorded on the ledger line. A miscount at receipt, a correction after a count, stock found or lost."
            value={adjustmentReason}
            onChange={(e) => setAdjustmentReason(e.target.value)}
            maxLength={500}
            disabled={saving}
            error={error?.fieldError('adjustmentReason')}
          />
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <DateField
            id="stock-batch-expiry-date"
            label="Expires"
            description={
              chosenItem && !chosenItem.tracksExpiry
                ? 'This item does not track expiry, so a date here would never be read.'
                : 'The day this lot lapses. Expiry is derived from it on every read — nothing sweeps a lot into an expired state, so correcting the date corrects the status.'
            }
            value={expiryDate}
            onChange={setExpiryDate}
            disabled={saving}
            error={error?.fieldError('expiryDate')}
          />

          <TextField
            id="stock-batch-unit-cost"
            label="Unit cost"
            type="number"
            min={0}
            step="0.01"
            description="What this lot cost per unit. A snapshot, captured here and never overwritten — the catalog's own price is the current reference figure, which is a different fact."
            value={unitCost}
            onChange={(e) => setUnitCost(e.target.value)}
            disabled={saving}
            error={error?.fieldError('unitCost')}
          />
        </div>

        {isEdit ? (
          <>
            <Select
              id="stock-batch-status"
              label="Condition"
              required
              description="The state a person is putting this lot in. Expired and Depleted are not offered: the first is read from the date and the second follows from the quantity reaching zero."
              value={status}
              onChange={setStatus}
              sort="none"
              clearable={false}
              disabled={saving}
              options={SETTABLE_STATUSES.map((s) => ({
                value: String(s),
                label: BATCH_STATUS_LABEL[s],
              }))}
              error={error?.fieldError('status')}
            />

            {quarantining ? (
              <TextField
                id="stock-batch-quarantine-reason"
                label="Why is it quarantined?"
                required
                description="Cleared automatically if the lot is released, so a batch back in use cannot keep a reason behind the reader's back."
                value={quarantineReason}
                onChange={(e) => setQuarantineReason(e.target.value)}
                maxLength={500}
                disabled={saving}
                error={error?.fieldError('quarantineReason')}
              />
            ) : null}
          </>
        ) : null}

        {/* FULL WIDTH and outside the grid: a remark is the longest value on the form and the
            one most likely to be a sentence. */}
        <TextField
          id="stock-batch-notes"
          label="Notes"
          description="Anything worth knowing about this delivery."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1000}
          disabled={saving}
          error={error?.fieldError('notes')}
        />
      </div>
    </Modal>
  );
}
